import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Iconify from '../../../components/Iconify';
import Label from '../../../components/Label';
import { authApiCall } from '../../../services/http';
import { endpoints } from '../../../config';
import { setAuthToken, setAuthUser } from '../../../redux/reducers/authSlice';
import { stripeCustomerUrl, stripeSubscriptionUrl, subscriptionStatus } from '../../../helpers/subscriptions';
import useProducts from '../../../hooks/useProducts';
import { productLabel } from '../../../helpers/products';
import copyToClipboard from '../../../utils/copyToClipboard';
import SubscriptionDetailsDialog from '../../../layouts/dialogs/SubscriptionDetailsDialog';
import UserActiveSubscriptionsDialog from '../../../layouts/dialogs/UserActiveSubscriptionsDialog';
import EditSeatDialog from './EditSeatDialog';

const fDate = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES') : '—');

// Opción de producto en los Autocomplete: displayName arriba, slug en gris pequeño debajo.
const renderProductOption = (props, option) => (
  <Box component="li" {...props} key={option.slug}>
    <Box>
      <Typography variant="body2">{productLabel(option)}</Typography>
      <Typography variant="caption" display="block" sx={{ color: 'text.secondary', fontSize: 11 }}>
        {option.slug}
      </Typography>
      {/* Nota interna del catálogo (Product.comments): solo llega con token de ADMIN. */}
      {option.comments && (
        <Typography
          variant="caption"
          display="block"
          sx={{
            color: 'text.disabled',
            fontSize: 11,
            fontStyle: 'italic',
            maxWidth: 320,
            whiteSpace: 'normal',
          }}
        >
          {option.comments}
        </Typography>
      )}
    </Box>
  </Box>
);

const billingIntervalLabel = (v) => {
  if (v === 'month') return 'Mensual';
  if (v === 'year') return 'Anual';
  if (v === 'one_time') return 'Pago único';
  return '—';
};

const fEur = (n) =>
  new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

// Devuelve { amount, kind } para mostrar el precio. kind = 'perUser' | 'flat' | null.
const priceInfo = (sub) => {
  if (sub.pricePerUserEur != null && sub.pricePerUserEur !== '') {
    return { amount: fEur(sub.pricePerUserEur), kind: 'perUser' };
  }
  if (sub.flatPriceEur != null && sub.flatPriceEur !== '') {
    return { amount: fEur(sub.flatPriceEur), kind: 'flat' };
  }
  return { amount: '—', kind: null };
};

SubscriptionUsersDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  subscription: PropTypes.object,
  onUpdated: PropTypes.func,
};

export default function SubscriptionUsersDialog({ open, onClose, subscription, onUpdated }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.auth.authToken);
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // "Añadir usuario": crea un seat (con plan + addons) para el user en esta sub.
  // POST /seats { subscriptionId (UUID), planSlug, userId, addonSlugs }.
  const [addUserId, setAddUserId] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addSuccess, setAddSuccess] = useState(null);
  const [removingSeatId, setRemovingSeatId] = useState(null);

  // Operaciones admin global sobre seats que antes no estaban gestionadas:
  //  - GET /seats?subscriptionId= → surface de asientos LIBRES (no salen en /subscriptions/:id/users).
  //  - PUT /seats/:id → editar plan/add-ons (vía EditSeatDialog).
  //  - DELETE /seats/:id → borrado definitivo.
  //  - PATCH /seats/:id/assign → asignar user a un seat libre.
  const [freeSeats, setFreeSeats] = useState([]);
  const [editSeat, setEditSeat] = useState(null);
  const [editSeatOpen, setEditSeatOpen] = useState(false);
  const [deletingSeatId, setDeletingSeatId] = useState(null);
  const [assignSeat, setAssignSeat] = useState(null);
  const [assignUserId, setAssignUserId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState(null);
  // Resultado destructivo de PATCH /seats/:id/assign: al asignar, el seat pasa a ser el plan del
  // usuario y el backend cancela sus otras subs vigentes.
  //   - canceledSubscriptionIds: subs manuales cerradas (trials, altas de admin). Ya no otorgan.
  //   - skippedStripeBacked: subs de Stripe que siguen VIVAS y sin tocar (Stripe manda sobre su
  //     ciclo de vida); si deben terminar, hay que cancelarlas allí.
  const [closedInfo, setClosedInfo] = useState(null);

  // Chequeo post-atribución: tras meter un user en esta sub, miramos si arrastraba otras subs
  // vivas como titular (típico: un trial anterior sin cancelar) para poder resolverlo al momento.
  const [conflictUserId, setConflictUserId] = useState(null);

  // Catálogo de productos (planes/addons) para los selectores; se carga al abrir el diálogo.
  const { plans, addons, bySlug, loading: productsLoading } = useProducts(open);

  const apiProps = { navigate, dispatch, setAuthUser, setAuthToken, authToken, redirectRoute: null };

  const fetchUsers = async (sub) => {
    const res = await authApiCall({
      method: 'GET',
      endpoint: endpoints.subscriptions,
      path: `/${sub.id}/users`,
      ...apiProps,
    });
    const data = res?.data;
    return Array.isArray(data) ? data : data?.users || [];
  };

  // GET /seats?subscriptionId=<publicId> incluye los seats LIBRES (userId null), que no
  // aparecen en /subscriptions/:id/users. No es crítico: si falla, devolvemos lista vacía.
  const fetchFreeSeats = async (sub) => {
    try {
      const res = await authApiCall({
        method: 'GET',
        endpoint: endpoints.seats,
        path: `?subscriptionId=${encodeURIComponent(sub.id)}`,
        ...apiProps,
      });
      const data = res?.data;
      const list = Array.isArray(data) ? data : data?.seats || [];
      return list.filter((s) => !s.userId);
    } catch (e) {
      return [];
    }
  };

  useEffect(() => {
    if (!open || !subscription) return;
    setUsers([]);
    setFreeSeats([]);
    setNotFound(false);
    setError(null);
    setCopiedId(false);
    setAddUserId('');
    setSelectedPlan(null);
    setSelectedAddons([]);
    setAddError(null);
    setAddSuccess(null);
    setEditSeat(null);
    setEditSeatOpen(false);
    setAssignSeat(null);
    setConflictUserId(null);
    setClosedInfo(null);
    setLoading(true);

    Promise.all([fetchUsers(subscription), fetchFreeSeats(subscription)])
      .then(([occupants, free]) => {
        setUsers(occupants);
        setFreeSeats(free);
      })
      .catch((e) => {
        const status = e?.response?.status;
        if (status === 404) setNotFound(true);
        else setError(e?.response?.data?.error || e?.message || 'Error cargando usuarios');
      })
      .finally(() => setLoading(false));
  }, [open, subscription, authToken]);

  if (!subscription) return null;

  const isManual = !subscription.stripeSubscriptionId;
  const usedSeats = subscription.userCount ?? users.length;
  const seatsLabel = subscription.maxSeats ? `${usedSeats} / ${subscription.maxSeats}` : `${usedSeats}`;
  const price = priceInfo(subscription);
  // Hay cupo si maxSeats es null (sin límite) o quedan asientos.
  const canAddUser = !subscription.maxSeats || usedSeats < subscription.maxSeats;

  const refreshAfterSeatChange = async () => {
    const [occupants, free] = await Promise.all([fetchUsers(subscription), fetchFreeSeats(subscription)]);
    setUsers(occupants);
    setFreeSeats(free);
    // Refrescar el userCount en el row del padre para que el cupo se actualice.
    if (onUpdated) onUpdated({ ...subscription, userCount: occupants.length });
  };

  const handleAddUser = async () => {
    const trimmed = addUserId.trim();
    if (!trimmed) {
      setAddError('Introduce el userId.');
      return;
    }
    const id = Number(trimmed);
    if (!Number.isInteger(id) || id <= 0) {
      setAddError('El userId debe ser un número entero positivo.');
      return;
    }
    if (!selectedPlan) {
      setAddError('Selecciona un plan para el seat.');
      return;
    }
    setAdding(true);
    setAddError(null);
    setAddSuccess(null);
    setClosedInfo(null);
    try {
      // POST /seats crea el seat y lo asigna al user en un solo paso.
      const res = await authApiCall({
        method: 'POST',
        endpoint: endpoints.seats,
        path: '',
        data: {
          subscriptionId: subscription.id,
          planSlug: selectedPlan.slug,
          userId: id,
          addonSlugs: selectedAddons.map((a) => a.slug),
        },
        ...apiProps,
      });
      setAddSuccess('Seat creado y usuario asignado.');
      // El cierre de otras subs está documentado en PATCH /assign; si este camino también lo hace,
      // la respuesta traerá el mismo `closedSubscriptions` y lo enseñamos igual.
      setClosedInfo(res?.data?.closedSubscriptions || null);
      setAddUserId('');
      setSelectedPlan(null);
      setSelectedAddons([]);
      await refreshAfterSeatChange();
      setConflictUserId(id);
    } catch (e) {
      setAddError(e?.response?.data?.error || e?.message || 'Error al crear el seat');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveUser = async (seatId) => {
    if (!seatId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Se liberará el seat (el usuario quedará desasignado). ¿Continuar?')) return;
    setRemovingSeatId(seatId);
    setAddError(null);
    setAddSuccess(null);
    try {
      // PATCH /seats/:id/unassign libera el seat conservando la fila (plan/addons).
      await authApiCall({
        method: 'PATCH',
        endpoint: endpoints.seats,
        path: `/${seatId}/unassign`,
        ...apiProps,
      });
      await refreshAfterSeatChange();
    } catch (e) {
      setAddError(e?.response?.data?.error || e?.message || 'Error al liberar el seat');
    } finally {
      setRemovingSeatId(null);
    }
  };

  const handleEditSeat = (seat) => {
    if (!seat?.id) return;
    setEditSeat(seat);
    setEditSeatOpen(true);
  };

  const handleDeleteSeat = async (seatId) => {
    if (!seatId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Se eliminará el seat de forma DEFINITIVA (borrado puro, no toca billing/Stripe). ¿Continuar?'))
      return;
    setDeletingSeatId(seatId);
    setAddError(null);
    setAddSuccess(null);
    try {
      // DELETE /seats/:id — borra la fila. No ajusta maxSeats ni Stripe.
      await authApiCall({
        method: 'DELETE',
        endpoint: endpoints.seats,
        path: `/${seatId}`,
        ...apiProps,
      });
      await refreshAfterSeatChange();
    } catch (e) {
      setAddError(e?.response?.data?.error || e?.message || 'Error al eliminar el seat');
    } finally {
      setDeletingSeatId(null);
    }
  };

  const openAssign = (seat) => {
    setAssignSeat(seat);
    setAssignUserId('');
    setAssignError(null);
    setClosedInfo(null);
  };

  const handleAssign = async () => {
    if (!assignSeat) return;
    const id = Number(assignUserId.trim());
    if (!Number.isInteger(id) || id <= 0) {
      setAssignError('El userId debe ser un número entero positivo.');
      return;
    }
    // La operación es destructiva: cancela las otras subs vigentes del usuario (las manuales;
    // las de Stripe se dejan vivas). Confirmamos antes porque no hay deshacer.
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        `Este seat pasará a ser el plan del usuario #${id} y se CANCELARÁN sus otras suscripciones vigentes ` +
          `(las manuales; las de Stripe se quedan vivas y hay que cancelarlas allí). ¿Continuar?`
      )
    )
      return;
    setAssigning(true);
    setAssignError(null);
    try {
      // PATCH /seats/:id/assign — asigna el user a un seat LIBRE. Además registra al usuario como
      // titular de la sub si no tenía ninguno (nunca sobrescribe uno existente).
      const res = await authApiCall({
        method: 'PATCH',
        endpoint: endpoints.seats,
        path: `/${assignSeat.id}/assign`,
        data: { userId: id },
        ...apiProps,
      });
      setClosedInfo(res?.data?.closedSubscriptions || null);
      setAssignSeat(null);
      setAssignUserId('');
      await refreshAfterSeatChange();
      setConflictUserId(id);
    } catch (e) {
      setAssignError(e?.response?.data?.error || e?.message || 'Error al asignar el usuario');
    } finally {
      setAssigning(false);
    }
  };

  const copyVoicitId = async () => {
    await copyToClipboard(subscription.id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 1500);
  };

  const st = subscriptionStatus(subscription);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <span>Suscripción</span>
          <Label color={st.color}>{st.label}</Label>
          {isManual && <Label color="default">No stripe</Label>}
          {subscription.stripeSubscriptionId && (
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
              {subscription.stripeSubscriptionId}
            </Typography>
          )}
          {subscription.stripeSubscriptionId && (
            <Tooltip title="Ver en Stripe">
              <IconButton
                size="small"
                component="a"
                href={stripeSubscriptionUrl(subscription.stripeSubscriptionId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Iconify icon="eva:external-link-outline" width={16} height={16} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <Box>
              <Typography variant="caption" color="text.secondary">
                ID
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Tooltip title={`Copiar — ${subscription.id}`}>
                  <Typography
                    variant="body2"
                    onClick={copyVoicitId}
                    sx={{
                      fontFamily: 'monospace',
                      cursor: 'pointer',
                      '&:hover': { color: 'text.primary' },
                      color: 'text.secondary',
                    }}
                  >
                    {String(subscription.id).slice(0, 8)}
                  </Typography>
                </Tooltip>
                {copiedId && (
                  <Iconify icon="eva:checkmark-fill" width={14} height={14} sx={{ color: 'success.main' }} />
                )}
              </Stack>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Usuario (titular)
              </Typography>
              <Typography variant="body2">{subscription.userId ?? '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Workspace
              </Typography>
              <Typography variant="body2">{subscription.workspaceId ?? '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Asientos
              </Typography>
              <Typography variant="body2">{seatsLabel}</Typography>
            </Box>
            {isManual && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {price.kind === 'flat' ? 'Precio fijo' : 'Precio / asiento'}
                </Typography>
                <Typography variant="body2" color={price.kind ? 'text.primary' : 'text.disabled'}>
                  {price.amount}
                </Typography>
              </Box>
            )}
            {isManual && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Facturación
                </Typography>
                <Typography variant="body2" color={subscription.billingInterval ? 'text.primary' : 'text.disabled'}>
                  {billingIntervalLabel(subscription.billingInterval)}
                </Typography>
              </Box>
            )}
            {subscription.stripeCustomerId && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Customer Stripe
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title="Ver en Stripe">
                    <Typography
                      variant="body2"
                      component="a"
                      href={stripeCustomerUrl(subscription.stripeCustomerId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        fontFamily: 'monospace',
                        color: 'primary.main',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {subscription.stripeCustomerId}
                    </Typography>
                  </Tooltip>
                  <Iconify icon="eva:external-link-outline" width={14} height={14} sx={{ color: 'primary.main' }} />
                </Stack>
              </Box>
            )}
            {subscription.createdAt && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Creada
                </Typography>
                <Typography variant="body2">{fDate(subscription.createdAt)}</Typography>
              </Box>
            )}
            {subscription.currentPeriodEnd && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Fin periodo
                </Typography>
                <Typography variant="body2">{fDate(subscription.currentPeriodEnd)}</Typography>
              </Box>
            )}
            {isManual && (
              <Box sx={{ ml: 'auto', alignSelf: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Iconify icon="eva:edit-2-outline" width={16} height={16} />}
                  onClick={() => setEditOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Editar
                </Button>
              </Box>
            )}
          </Stack>

          <Divider />

          {closedInfo && (
            <Alert severity="warning" onClose={() => setClosedInfo(null)}>
              <Stack spacing={0.5}>
                {closedInfo.canceledSubscriptionIds?.length > 0 ? (
                  <Typography variant="body2">
                    Se han cancelado {closedInfo.canceledSubscriptionIds.length} suscripción
                    {closedInfo.canceledSubscriptionIds.length === 1 ? '' : 'es'} manual
                    {closedInfo.canceledSubscriptionIds.length === 1 ? '' : 'es'} del usuario:{' '}
                    {closedInfo.canceledSubscriptionIds.join(', ')}.
                  </Typography>
                ) : (
                  <Typography variant="body2">No se ha cancelado ninguna suscripción manual del usuario.</Typography>
                )}
                {closedInfo.skippedStripeBacked?.length > 0 && (
                  <Typography variant="body2">
                    Sigue{closedInfo.skippedStripeBacked.length === 1 ? '' : 'n'} viva
                    {closedInfo.skippedStripeBacked.length === 1 ? '' : 's'} en Stripe (hay que cancelarla
                    {closedInfo.skippedStripeBacked.length === 1 ? '' : 's'} allí):{' '}
                    {closedInfo.skippedStripeBacked.map((sid) => (
                      <Typography
                        key={sid}
                        component="a"
                        variant="body2"
                        href={stripeSubscriptionUrl(sid)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontFamily: 'monospace', mr: 1 }}
                      >
                        {sid}
                      </Typography>
                    ))}
                  </Typography>
                )}
              </Stack>
            </Alert>
          )}

          <Typography variant="subtitle2">Usuarios vinculados</Typography>

          {loading && (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={32} />
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {notFound && <Alert severity="warning">Esta suscripción no se encontró en backend (404).</Alert>}

          {!loading && !error && !notFound && users.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No hay usuarios vinculados.
            </Typography>
          )}

          {!loading && users.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Add-ons</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((u) => {
                    const seat = u.seat || {};
                    const planName = bySlug[seat.planSlug] ? productLabel(bySlug[seat.planSlug]) : seat.planSlug || '—';
                    const seatAddons = seat.addonSlugs || [];
                    return (
                      <TableRow key={u.id} hover>
                        <TableCell>
                          <Tooltip title={`Copiar — ${u.id}`}>
                            <Typography
                              variant="body2"
                              onClick={() => copyToClipboard(u.id)}
                              sx={{
                                fontFamily: 'monospace',
                                cursor: 'pointer',
                                '&:hover': { color: 'text.primary' },
                                color: 'text.secondary',
                              }}
                            >
                              {String(u.id).slice(0, 8)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{u.email || '—'}</TableCell>
                        <TableCell>{u.givenName || '—'}</TableCell>
                        <TableCell>{planName}</TableCell>
                        <TableCell>
                          {seatAddons.length === 0 ? (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          ) : (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {seatAddons.map((slug) => (
                                <Chip
                                  key={slug}
                                  size="small"
                                  label={bySlug[slug] ? productLabel(bySlug[slug]) : slug}
                                />
                              ))}
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Editar plan / add-ons">
                              <span>
                                <IconButton size="small" disabled={!seat.id} onClick={() => handleEditSeat(seat)}>
                                  <Iconify icon="eva:edit-2-outline" width={18} height={18} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Liberar seat (desasignar usuario)">
                              <span>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  disabled={!seat.id || removingSeatId === seat.id}
                                  onClick={() => handleRemoveUser(seat.id)}
                                >
                                  {removingSeatId === seat.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <Iconify icon="eva:person-remove-outline" width={18} height={18} />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Eliminar seat (definitivo)">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={!seat.id || deletingSeatId === seat.id}
                                  onClick={() => handleDeleteSeat(seat.id)}
                                >
                                  {deletingSeatId === seat.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <Iconify icon="eva:trash-2-outline" width={18} height={18} />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!loading && freeSeats.length > 0 && (
            <>
              <Divider />
              <Typography variant="subtitle2">Asientos libres ({freeSeats.length})</Typography>
              <Typography variant="caption" color="text.secondary">
                Seats sin usuario asignado. Puedes asignarles un user, editar su plan/add-ons o eliminarlos.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Seat</TableCell>
                      <TableCell>Plan</TableCell>
                      <TableCell>Add-ons</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {freeSeats.map((s) => {
                      const planName = bySlug[s.planSlug] ? productLabel(bySlug[s.planSlug]) : s.planSlug || '—';
                      const sAddons = s.addonSlugs || [];
                      return (
                        <TableRow key={s.id} hover>
                          <TableCell>
                            <Tooltip title={`Copiar — ${s.id}`}>
                              <Typography
                                variant="body2"
                                onClick={() => copyToClipboard(s.id)}
                                sx={{
                                  fontFamily: 'monospace',
                                  cursor: 'pointer',
                                  '&:hover': { color: 'text.primary' },
                                  color: 'text.secondary',
                                }}
                              >
                                {String(s.id).slice(0, 8)}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{planName}</TableCell>
                          <TableCell>
                            {sAddons.length === 0 ? (
                              <Typography variant="caption" color="text.disabled">
                                —
                              </Typography>
                            ) : (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {sAddons.map((slug) => (
                                  <Chip
                                    key={slug}
                                    size="small"
                                    label={bySlug[slug] ? productLabel(bySlug[slug]) : slug}
                                  />
                                ))}
                              </Stack>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title="Asignar usuario">
                                <span>
                                  <IconButton size="small" color="primary" onClick={() => openAssign(s)}>
                                    <Iconify icon="eva:person-add-outline" width={18} height={18} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Editar plan / add-ons">
                                <span>
                                  <IconButton size="small" onClick={() => handleEditSeat(s)}>
                                    <Iconify icon="eva:edit-2-outline" width={18} height={18} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Eliminar seat (definitivo)">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    disabled={deletingSeatId === s.id}
                                    onClick={() => handleDeleteSeat(s.id)}
                                  >
                                    {deletingSeatId === s.id ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <Iconify icon="eva:trash-2-outline" width={18} height={18} />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {canAddUser && (
            <>
              <Divider />
              <Typography variant="subtitle2">Añadir un usuario</Typography>
              <Typography variant="caption" color="text.secondary">
                Se crea un seat con el plan elegido y se asigna al usuario. El usuario no puede tener ya otro seat en
                esta suscripción.
              </Typography>
              <TextField
                label="userId"
                value={addUserId}
                onChange={(e) => setAddUserId(e.target.value)}
                placeholder="ID entero del user de Voicit"
                size="small"
                fullWidth
                onKeyDown={(e) => e.key === 'Enter' && !adding && handleAddUser()}
              />
              <Autocomplete
                fullWidth
                options={plans}
                loading={productsLoading}
                value={selectedPlan}
                onChange={(e, v) => setSelectedPlan(v)}
                getOptionLabel={productLabel}
                renderOption={renderProductOption}
                isOptionEqualToValue={(o, v) => o.slug === v.slug}
                size="small"
                renderInput={(params) => <TextField {...params} label="Plan" placeholder="Selecciona plan" />}
              />
              <Autocomplete
                multiple
                options={addons}
                loading={productsLoading}
                value={selectedAddons}
                onChange={(e, v) => setSelectedAddons(v)}
                getOptionLabel={productLabel}
                renderOption={renderProductOption}
                isOptionEqualToValue={(o, v) => o.slug === v.slug}
                size="small"
                renderTags={(value, getTagProps) =>
                  value.map((a, index) => (
                    <Chip size="small" label={productLabel(a)} {...getTagProps({ index })} key={a.slug} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Add-ons (opcional)" placeholder="Añade add-ons" />
                )}
              />
              <Box sx={{ textAlign: 'right' }}>
                <LoadingButton variant="contained" onClick={handleAddUser} loading={adding}>
                  Crear seat
                </LoadingButton>
              </Box>
              {addError && <Alert severity="error">{addError}</Alert>}
              {addSuccess && <Alert severity="success">{addSuccess}</Alert>}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>

      <SubscriptionDetailsDialog
        open={editOpen}
        subscription={subscription}
        onClose={() => setEditOpen(false)}
        onSaved={(updated) => {
          if (updated && onUpdated) onUpdated(updated);
        }}
      />

      <EditSeatDialog
        open={editSeatOpen}
        seat={editSeat}
        plans={plans}
        addons={addons}
        productsLoading={productsLoading}
        apiProps={apiProps}
        onClose={() => setEditSeatOpen(false)}
        onSaved={async () => {
          setEditSeatOpen(false);
          await refreshAfterSeatChange();
        }}
      />

      <Dialog open={Boolean(assignSeat)} onClose={() => !assigning && setAssignSeat(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Asignar usuario al seat</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Seat {assignSeat ? String(assignSeat.id).slice(0, 8) : ''}. El usuario no puede tener ya otro seat en esta
              suscripción.
            </Typography>
            <Alert severity="warning">
              Este seat pasa a ser el plan del usuario: el backend{' '}
              <strong>cancela sus otras suscripciones vigentes</strong> (trials y altas manuales). Las de Stripe se
              quedan vivas y hay que cancelarlas en Stripe. Si la sub no tenía titular, este usuario pasa a serlo.
            </Alert>
            <TextField
              label="userId"
              value={assignUserId}
              onChange={(e) => setAssignUserId(e.target.value)}
              placeholder="ID entero del user de Voicit"
              size="small"
              fullWidth
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && !assigning && handleAssign()}
            />
            {assignError && <Alert severity="error">{assignError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignSeat(null)} disabled={assigning}>
            Cancelar
          </Button>
          <LoadingButton variant="contained" onClick={handleAssign} loading={assigning}>
            Asignar
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <UserActiveSubscriptionsDialog
        open={Boolean(conflictUserId)}
        userId={conflictUserId}
        excludeSubscriptionId={subscription.id}
        contextMessage={`Acabas de asignar el usuario #${conflictUserId} a esta suscripción.`}
        onClose={() => setConflictUserId(null)}
      />
    </Dialog>
  );
}
